import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from '@/components/ui/sonner';
import ErrorBoundary from '@/components/ErrorBoundary';
import './i18n'; // Import i18n config
import '@/App.css';
import './index.css';
import './styles/premium.css';

// Clear cached user data on version update (one-time reset)
const APP_VERSION = 'v2.5.0-orbital';
const storedVersion = localStorage.getItem('app_version');
if (storedVersion !== APP_VERSION) {
  // Clear training-related data
  localStorage.removeItem('training_profile');
  localStorage.removeItem('saved_workouts');
  localStorage.removeItem('workout_history');
  localStorage.removeItem('monthly_workout_count');
  // Clear old cached data
  localStorage.removeItem('keto_buddy_history');
  localStorage.removeItem('checkin_data');
  // Set new version
  localStorage.setItem('app_version', APP_VERSION);
  console.log('🔄 App updated to', APP_VERSION, '- Cache cleared');
}

// Register Service Worker for PWA
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/service-worker.js')
      .then(registration => {
        console.log('SW registered:', registration.scope);
      })
      .catch(error => {
        console.log('SW registration failed:', error);
      });
  });
}

// Initialize OneSignal Push Notifications
window.OneSignalDeferred = window.OneSignalDeferred || [];
window.OneSignalDeferred.push(async function(OneSignal) {
  const appId = process.env.REACT_APP_ONESIGNAL_APP_ID;
  if (appId) {
    try {
      await OneSignal.init({
        appId: appId,
        allowLocalhostAsSecureOrigin: true,
        notifyButton: {
          enable: true,
          size: 'medium',
          position: 'bottom-right',
          prenotify: true,
          showCredit: false,
          text: {
            'tip.state.unsubscribed': 'Enable notifications',
            'tip.state.subscribed': 'Notifications active',
            'tip.state.blocked': 'Notifications blocked',
            'message.prenotify': 'Click to receive notifications',
            'message.action.subscribed': 'Thanks for enabling!',
            'message.action.resubscribed': 'Notifications reactivated!',
            'message.action.unsubscribed': 'You will no longer receive notifications'
          }
        },
        welcomeNotification: {
          title: 'Keto Lab Method 🔥',
          message: 'Welcome! You will receive reminders and tips for your transformation!'
        },
        promptOptions: {
          slidedown: {
            prompts: [
              {
                type: 'push',
                autoPrompt: true,
                text: {
                  actionMessage: 'Would you like to receive fasting reminders and personalized tips?',
                  acceptButton: 'Yes, enable!',
                  cancelButton: 'Not now'
                },
                delay: {
                  pageViews: 2,
                  timeDelay: 10
                }
              }
            ]
          }
        }
      });
      console.log('OneSignal initialized successfully');
    } catch (error) {
      console.log('OneSignal init error:', error);
    }
  }
});

// Contexts
import { SubscriptionProvider } from '@/contexts/SubscriptionContext';
import { ThemeProvider } from '@/contexts/ThemeContext';

// Admin Tools
import PlanSimulator from '@/components/admin/PlanSimulator';

// Upsell & Notification Components
import UpsellBanner from '@/components/UpsellBanner';

// 🏆 World-Class: Trial Onboarding
import { TrialOnboardingOverlay, TrialCountdownBanner } from '@/components/onboarding/TrialOnboardingOverlay';
import KetoScanFAB from '@/components/KetoScanFAB';
import SpiritAgentFAB from '@/components/spirit-agent/SpiritAgentFAB';
import SpiritAwakening from '@/components/awakening/SpiritAwakening';
import JourneyMapPage from '@/pages/JourneyMapPage';

// Loading Fallback Component
const PageLoader = () => (
  <div className="min-h-screen bg-black flex items-center justify-center">
    <div className="text-center">
      <div className="w-12 h-12 border-4 border-[#a3e635] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <div className="text-[#a3e635] text-lg">Loading...</div>
    </div>
  </div>
);

// Critical Pages - Load immediately (Landing & Auth flow)
import LandingPage from '@/pages/LandingPagePremium';
import KetoFusionLanding from '@/pages/KetoFusionLanding';
import KetoFusionDashboard from '@/pages/KetoFusionDashboard';
import KetoFusionFinale from '@/pages/KetoFusionFinale';
import PhaseExecute from '@/pages/PhaseExecute';
import Challenge8KLanding from '@/pages/Challenge8KLanding';

// Lazy-loaded Pages - Code Splitting for better performance
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const UniverseHub = lazy(() => import('@/pages/KetoLabHub'));
const OnboardingPage = lazy(() => import('@/pages/OnboardingPage'));
const ProgressPage = lazy(() => import('@/pages/ProgressPageWorldClass'));
const FastingPagePremium = lazy(() => import('@/pages/FastingPage'));
const NeuroKetoPage = lazy(() => import('@/pages/NeuroKetoPage'));
// const MindsetPage = lazy(() => import('@/pages/MindsetPage')); // Replaced by NeuroKeto
const MealsPage = lazy(() => import('@/pages/MealsPage'));
const CommunityPage = lazy(() => import('@/pages/CommunityPageNew'));
const AchievementsPage = lazy(() => import('@/pages/AchievementsPage'));
const AICoachPage = lazy(() => import('@/pages/KetoBuddyPage'));
const NutritionChatPage = lazy(() => import('@/pages/NutritionChatPage'));
const MetabolicProfilePage = lazy(() => import('@/pages/MetabolicProfilePage'));
const AdminPage = lazy(() => import('@/pages/AdminPage'));
const NudgeStatsPage = lazy(() => import('@/pages/admin/NudgeStatsPage'));
const BodyTwinManagerPage = lazy(() => import('@/pages/admin/BodyTwinManagerPage'));
const AdminRescuePage = lazy(() => import('@/pages/admin/AdminRescuePage'));
const JourneyPage = lazy(() => import('@/pages/JourneyPage'));
const LegalPage = lazy(() => import('@/pages/LegalPage'));
const PrivacySettingsPage = lazy(() => import('@/pages/PrivacySettingsPage'));
const ContentLibraryPage = lazy(() => import('@/pages/ContentLibraryPage'));
const KetoLibraryPage = lazy(() => import('@/pages/KetoLibraryPage'));
const CirclesPage = lazy(() => import('@/pages/CirclesPage'));
const Avatar3DTest = lazy(() => import('@/pages/Avatar3DTest'));
// Body Twin - Apenas páginas activas (Evolved + Video + Hub)
const BodyTwinVideoPage = lazy(() => import('@/pages/BodyTwinVideoPage'));
const BodyTwinEvolvedPage = lazy(() => import('@/pages/BodyTwinEvolvedPage'));
const BodyTwinPremiumPage = lazy(() => import('@/pages/BodyTwinPremiumPage'));
const BodyTwinHubPage = lazy(() => import('@/pages/BodyTwinHubPage'));
const BodyTwin2CodexPage = lazy(() => import('@/pages/BodyTwin2CodexPage'));
const ProfileSettingsPage = lazy(() => import('@/pages/ProfileSettingsPage'));
const TierDashboardPage = lazy(() => import('@/pages/TierDashboardPage'));
const DynamicMacrosPage = lazy(() => import('@/pages/DynamicMacrosPage'));
const MealTemplatePage = lazy(() => import('@/pages/MealTemplatePage'));
const Challenge8KFinalePage = lazy(() => import('@/pages/Challenge8KFinalePage'));
const AnalyticsDashboard = lazy(() => import('@/pages/AnalyticsDashboard'));
const RecipeLibraryPage = lazy(() => import('@/pages/RecipeLibraryPage'));
const MetabolicSnapshotPage = lazy(() => import('@/pages/MetabolicSnapshotPage'));
const TierPage = lazy(() => import('@/pages/TierPage'));
const DevicesPage = lazy(() => import('@/pages/DevicesPage'));
const Challenge8KRouteGuard = lazy(() => import('@/components/Challenge8KRouteGuard'));
const Challenge8KSpiritPage = lazy(() => import('@/pages/Challenge8KSpiritPage'));
const KetoScanPage = lazy(() => import('@/pages/KetoScanPage'));
const MealPlannerPage = lazy(() => import('@/pages/MealPlannerPage'));
const SubscriptionPlansPage = lazy(() => import('@/pages/SubscriptionPlansPage'));
const EbookPage = lazy(() => import('@/pages/EbookPage'));
const ReferralPage = lazy(() => import('@/pages/ReferralPage'));
const ExportPage = lazy(() => import('@/pages/ExportPage'));
const RemindersPage = lazy(() => import('@/pages/RemindersPage'));
// TrainingAIPage replaced by MetabolicWorkoutPage
const MetabolicWorkoutPage = lazy(() => import('@/pages/MetabolicWorkoutPage'));
const SoundTherapyPage = lazy(() => import('@/pages/SoundTherapyPage'));
const FreeWorkoutCameraPage = lazy(() => import('@/pages/FreeWorkoutCameraPage'));
const SubscriptionPage = lazy(() => import('@/pages/SubscriptionPage'));
const NotificationsPage = lazy(() => import('@/pages/NotificationsPage'));
const HelpCenterPage = lazy(() => import('@/pages/HelpCenterPage'));
const CoachAdminPage = lazy(() => import('@/pages/CoachAdminPage'));
const FAQPage = lazy(() => import('@/pages/FAQPage'));
const VIPDashboard = lazy(() => import('@/pages/VIPDashboard'));
// Spirit Local - Keto Navigator (Passive Safety Radar)
const SpiritLocalPage = lazy(() => import('@/pages/SpiritLocalPage'));
const UnlimitedDashboard = lazy(() => import('@/pages/UnlimitedDashboard'));

// Password Reset Pages
const ForgotPasswordPage = lazy(() => import('@/pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/pages/ResetPasswordPage'));

// Ecosystem Pages (WOW Features 2025)
const EcosystemPage = lazy(() => import('@/pages/EcosystemPage'));
const DISCAssessmentPage = lazy(() => import('@/pages/DISCAssessmentPage'));
const VoiceCoachPage = lazy(() => import('@/pages/VoiceCoachPage'));
const TransformationVideoPage = lazy(() => import('@/pages/TransformationVideoPage'));
const AdaptiveAI2Page = lazy(() => import('@/components/AdaptiveAI2'));

// Components - Non-lazy as they're small and used globally
import LegalConsentModal from '@/components/LegalConsentModal';
import KetoBuddyTour from '@/components/KetoBuddyTour';

// API Configuration - Works in both preview and production
// In production (ketolab.pt): Uses relative URLs (/api)
// In preview: Uses REACT_APP_BACKEND_URL
const PRODUCTION_DOMAINS = ['ketolab.pt', 'www.ketolab.pt', 'app.ketolab.pt', 'pro.ketolab.pt'];
const isProductionDomain = typeof window !== 'undefined' && 
  (PRODUCTION_DOMAINS.some(d => window.location.hostname === d || window.location.hostname.endsWith('.' + d)) ||
   window.location.hostname.endsWith('.emergent.host'));

const BACKEND_URL = isProductionDomain ? '' : (process.env.REACT_APP_BACKEND_URL || '');
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

// Export for use in other components
export { BACKEND_URL, API, isProductionDomain };

// Configure axios with dynamic baseURL
axios.defaults.baseURL = BACKEND_URL;

// Configure axios interceptors
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  // Add language header
  const language = localStorage.getItem('language') || 'pt';
  config.headers['Accept-Language'] = language;
  
  return config;
});

// Log API configuration for debugging
if (process.env.NODE_ENV === 'development') {
  console.log('[API Config] Production Domain:', isProductionDomain, '| Backend URL:', BACKEND_URL, '| API:', API);
}

export const AuthContext = React.createContext();

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [showLegalConsent, setShowLegalConsent] = useState(false);
  const [requiresLegalAcceptance, setRequiresLegalAcceptance] = useState(false);
  const [showTour, setShowTour] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await axios.get(`${API}/profile`);
        setProfile(response.data);
      } catch (error) {
        console.error('Error fetching profile:', error);
      }
    };

    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`);
          setUser(response.data);
          await fetchProfile();
        } catch (error) {
          localStorage.removeItem('token');
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/profile`);
      setProfile(response.data);
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem('token', response.data.token);
      setUser(response.data.user);
      
      // Dispatch custom event to notify SubscriptionProvider
      window.dispatchEvent(new Event('authStateChanged'));
      
      // Check if legal acceptance is required
      if (response.data.requires_legal_acceptance) {
        setRequiresLegalAcceptance(true);
        setShowLegalConsent(true);
      }
      
      // Fetch profile but don't fail login if profile fetch fails
      try {
        await fetchProfile();
      } catch (profileError) {
        console.warn('Profile fetch failed, continuing with login:', profileError);
      }
      
      return response.data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name });
    localStorage.setItem('token', response.data.token);
    setUser(response.data.user);
    
    // Check if legal acceptance is required (should always be true for new users)
    if (response.data.requires_legal_acceptance) {
      setRequiresLegalAcceptance(true);
      setShowLegalConsent(true);
    }
    
    // Don't show tour on login - let the useEffect handle it with proper checks
    // This prevents the tour from appearing twice
    
    return response.data;
  };

  // Check if tour should be shown after login (for users who never completed it)
  // 🎯 KETO BUDDY TOUR — Show ONCE per user, never on overlay conflict
  // Single source of truth: localStorage flag `keto_buddy_tour_state_<userId>` ∈ {'completed', undefined}
  // - Skips if: admin, completed, legal consent pending, onboarding/auth route
  // - Cancels timer if legal consent appears within 2s window
  // - Marks 'completed' ONLY when user finishes or dismisses tour (not on schedule)
  useEffect(() => {
    if (!user || loading) return;
    
    // Skip for admins
    const isAdmin = user.role === 'admin' || user.access_level === 'ADMIN';
    if (isAdmin) return;
    
    // Skip if legal consent open/required (will re-evaluate after consent closes)
    if (showLegalConsent || requiresLegalAcceptance) return;
    
    // Skip on auth/onboarding routes (avoid tour over login/registration)
    const path = window.location.pathname;
    const blockedPaths = ['/login', '/register', '/reset-password', '/onboarding', '/legal'];
    if (blockedPaths.some(p => path.startsWith(p))) return;
    
    // Spirit Awakening replaces the legacy tour (pending or already completed)
    if (localStorage.getItem('spirit_awakening_pending') || user.spirit_awakening_at) return;

    // Per-user completion flag (one-time per account)
    const tourFlagKey = `keto_buddy_tour_state_${user.id}`;
    const tourState = localStorage.getItem(tourFlagKey);
    
    if (tourState === 'completed') return;
    
    // Schedule tour with 2s grace period
    const timer = setTimeout(() => {
      // Re-check completion flag AT FIRE TIME to prevent race with the
      // async completion writes (fixes tour re-appearing when user just
      // finished it or when playwright sets the flag mid-timer).
      const currentState = localStorage.getItem(tourFlagKey);
      if (currentState === 'completed') return;
      if (!showLegalConsent && !requiresLegalAcceptance) {
        setShowTour(true);
      }
    }, 2000);
    
    return () => clearTimeout(timer);
  }, [user, loading, showLegalConsent, requiresLegalAcceptance]);

  const handleTourComplete = () => {
    setShowTour(false);
    if (user?.id) {
      localStorage.setItem(`keto_buddy_tour_state_${user.id}`, 'completed');
    }
  };

  // Function to manually trigger tour (for "Start Tour" buttons)
  const startTour = () => {
    setShowTour(true);
  };

  const handleLegalAcceptance = () => {
    setRequiresLegalAcceptance(false);
    setShowLegalConsent(false);
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    setProfile(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-[#a3e635] text-xl">Carregando...</div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
    <AuthContext.Provider value={{ user, profile, login, register, logout, fetchProfile, startTour }}>
    <ThemeProvider>
      <SubscriptionProvider>
        <div className="App">
          <BrowserRouter>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Landing Page - Not lazy loaded for fast first paint */}
                <Route path="/" element={!user ? <LandingPage /> : <Navigate to="/hub" />} />
                <Route path="/login" element={!user ? <LandingPage /> : <Navigate to="/hub" />} />
                
                {/* Core App Routes - Lazy loaded */}
                <Route path="/onboarding" element={user ? <OnboardingPage /> : <Navigate to="/" />} />
                <Route path="/hub" element={user ? <UniverseHub /> : <Navigate to="/" />} />
                <Route path="/dashboard" element={user ? <Dashboard /> : <Navigate to="/" />} />
                <Route path="/ebook" element={user ? <EbookPage /> : <Navigate to="/" />} />
                <Route path="/progress" element={user ? <ProgressPage /> : <Navigate to="/" />} />
                <Route path="/fasting" element={user ? <FastingPagePremium /> : <Navigate to="/" />} />
                <Route path="/jejum" element={user ? <FastingPagePremium /> : <Navigate to="/" />} />
                <Route path="/mindset" element={user ? <NeuroKetoPage /> : <Navigate to="/" />} />
                <Route path="/onspirit" element={user ? <NeuroKetoPage /> : <Navigate to="/" />} />
                <Route path="/neuroketo" element={user ? <NeuroKetoPage /> : <Navigate to="/" />} />
                <Route path="/meals" element={user ? <MealsPage /> : <Navigate to="/" />} />
                <Route path="/community" element={user ? <CommunityPage /> : <Navigate to="/" />} />
                <Route path="/achievements" element={user ? <AchievementsPage /> : <Navigate to="/" />} />
                
                {/* Keto Buddy - Unificado (AI Coach + Buddy System) */}
                <Route path="/ai-coach" element={<Navigate to="/keto-buddy" />} />
                <Route path="/keto-buddy" element={user ? <AICoachPage /> : <Navigate to="/" />} />
                <Route path="/buddy-system" element={<Navigate to="/keto-buddy" />} />
                <Route path="/nutrition-chat" element={user ? <NutritionChatPage /> : <Navigate to="/" />} />
                <Route path="/metabolic-profile" element={<Navigate to="/metabolic-snapshot" replace />} />
                <Route path="/journey" element={user ? <JourneyPage /> : <Navigate to="/" />} />
                <Route path="/journey/:program" element={user ? <JourneyMapPage /> : <Navigate to="/" />} />
                <Route path="/admin" element={user ? <AdminPage /> : <Navigate to="/" />} />
                <Route path="/admin/nudge-stats" element={user ? <NudgeStatsPage /> : <Navigate to="/" />} />
                <Route path="/admin/body-twin" element={user ? <BodyTwinManagerPage /> : <Navigate to="/" />} />
                <Route path="/admin/rescue" element={user ? <AdminRescuePage /> : <Navigate to="/" />} />
                <Route path="/legal" element={user ? <LegalPage /> : <Navigate to="/" />} />
                <Route path="/privacy" element={user ? <PrivacySettingsPage /> : <Navigate to="/" />} />
                <Route path="/content" element={user ? <ContentLibraryPage /> : <Navigate to="/" />} />
                <Route path="/library" element={user ? <KetoLibraryPage /> : <Navigate to="/" />} />
                <Route path="/aprender" element={user ? <KetoLibraryPage /> : <Navigate to="/" />} />
                <Route path="/circles" element={user ? <CirclesPage /> : <Navigate to="/" />} />
                <Route path="/avatar-test" element={<Navigate to="/body-twin-evolved" replace />} />
                
                {/* Body Twin - Rotas activas */}
                <Route path="/body-twin" element={<Navigate to="/body-twin-2-codex" />} />
                <Route path="/body-twin-2-codex" element={user ? <BodyTwin2CodexPage /> : <Navigate to="/" />} />
                <Route path="/codex" element={<Navigate to="/body-twin-2-codex" />} />
                <Route path="/body-twin-hub" element={user ? <BodyTwinHubPage /> : <Navigate to="/" />} />
                <Route path="/body-twin-video" element={user ? <BodyTwinVideoPage /> : <Navigate to="/" />} />
                <Route path="/body-twin-evolved" element={user ? <BodyTwinEvolvedPage /> : <Navigate to="/" />} />
                <Route path="/body-twin-premium" element={user ? <BodyTwinPremiumPage /> : <Navigate to="/" />} />
                {/* Redirects de rotas antigas do Body Twin */}
                <Route path="/body-twin-real" element={<Navigate to="/body-twin-evolved" />} />
                <Route path="/body-twin-3" element={<Navigate to="/body-twin-evolved" />} />
                
                {/* KetoProgramPage removed - unified into Challenge8KPage */}
                <Route path="/keto-program" element={<Navigate to="/challenge-8k" />} />
                <Route path="/profile-settings" element={user ? <ProfileSettingsPage /> : <Navigate to="/" />} />
                <Route path="/tier-dashboard" element={user ? <TierDashboardPage /> : <Navigate to="/" />} />
                <Route path="/dynamic-macros" element={user ? <DynamicMacrosPage /> : <Navigate to="/" />} />
                <Route path="/meal-template" element={user ? <MealTemplatePage /> : <Navigate to="/" />} />
                <Route path="/challenge-8k/finale" element={user ? <Challenge8KFinalePage /> : <Navigate to="/" />} />
                <Route path="/analytics" element={user ? <AnalyticsDashboard /> : <Navigate to="/" />} />
                <Route path="/recipe-library" element={user ? <RecipeLibraryPage /> : <Navigate to="/" />} />
                <Route path="/metabolic-snapshot" element={user ? <MetabolicSnapshotPage /> : <Navigate to="/" />} />
                <Route path="/tiers" element={user ? <TierPage /> : <Navigate to="/" />} />
                <Route path="/subscription" element={user ? <SubscriptionPage /> : <Navigate to="/" />} />
                <Route path="/subscription/success" element={user ? <SubscriptionPage /> : <Navigate to="/" />} />
                <Route path="/plans" element={user ? <SubscriptionPlansPage /> : <Navigate to="/" />} />
                <Route path="/notifications" element={user ? <NotificationsPage /> : <Navigate to="/" />} />
                <Route path="/devices" element={user ? <DevicesPage /> : <Navigate to="/" />} />
                <Route path="/challenge-8k" element={user ? <Challenge8KSpiritPage /> : <Navigate to="/" />} />
                <Route path="/challenge-8k/full" element={user ? <Challenge8KRouteGuard /> : <Navigate to="/" />} />
                <Route path="/challenge-8k-premium" element={<Navigate to="/challenge-8k" />} />
                <Route path="/challenge-8k-classic" element={<Navigate to="/challenge-8k" />} />
                <Route path="/keto-fusion" element={<KetoFusionLanding />} />
                <Route path="/keto-fusion/today" element={user ? <KetoFusionDashboard /> : <Navigate to="/" />} />
                <Route path="/keto-fusion/finale" element={user ? <KetoFusionFinale /> : <Navigate to="/" />} />
                <Route path="/programs/:program/phase" element={user ? <PhaseExecute /> : <Navigate to="/" />} />
                <Route path="/desafio-8k" element={<Challenge8KLanding />} />
                <Route path="/8k-apresentacao" element={<Challenge8KLanding />} />
                <Route path="/ketoscan" element={user ? <KetoScanPage /> : <Navigate to="/" />} />
                <Route path="/meal-planner" element={user ? <MealPlannerPage /> : <Navigate to="/" />} />
                <Route path="/spirit-local" element={user ? <SpiritLocalPage /> : <Navigate to="/" />} />
                <Route path="/referral" element={user ? <ReferralPage /> : <Navigate to="/" />} />
                <Route path="/export" element={user ? <ExportPage /> : <Navigate to="/" />} />
                <Route path="/reminders" element={user ? <RemindersPage /> : <Navigate to="/" />} />
                <Route path="/training" element={user ? <MetabolicWorkoutPage /> : <Navigate to="/" />} />
                <Route path="/training-ai" element={user ? <MetabolicWorkoutPage /> : <Navigate to="/" />} />
                <Route path="/treino" element={user ? <MetabolicWorkoutPage /> : <Navigate to="/" />} />
                <Route path="/sound-therapy" element={user ? <SoundTherapyPage /> : <Navigate to="/" />} />
                <Route path="/workout-camera" element={user ? <FreeWorkoutCameraPage /> : <Navigate to="/" />} />
                <Route path="/free-workout" element={user ? <FreeWorkoutCameraPage /> : <Navigate to="/" />} />
                <Route path="/help" element={user ? <HelpCenterPage /> : <Navigate to="/" />} />
                <Route path="/coach-admin" element={user ? <CoachAdminPage /> : <Navigate to="/" />} />
                <Route path="/faq" element={<FAQPage />} />
                <Route path="/vip-dashboard" element={<Navigate to="/" replace />} />
                <Route path="/unlimited-dashboard" element={<Navigate to="/" replace />} />
                
                {/* Password Reset Routes - Public */}
                <Route path="/forgot-password" element={!user ? <ForgotPasswordPage /> : <Navigate to="/hub" />} />
                <Route path="/reset-password" element={!user ? <ResetPasswordPage /> : <Navigate to="/hub" />} />
                
                {/* Ecosystem WOW Features (legacy routes redirect to canonical hub) */}
                <Route path="/ecosystem" element={<Navigate to="/" replace />} />
                <Route path="/ecosystem/disc" element={user ? <DISCAssessmentPage /> : <Navigate to="/" />} />
                <Route path="/ecosystem/voice" element={<Navigate to="/voice-coach" replace />} />
                <Route path="/ecosystem/transformation" element={user ? <TransformationVideoPage /> : <Navigate to="/" />} />
                <Route path="/adaptive-ai" element={user ? <AdaptiveAI2Page /> : <Navigate to="/" />} />
              </Routes>
            </Suspense>
            
            {/* 🏆 World-Class: Trial Onboarding (dentro do BrowserRouter) */}
            {user && <TrialOnboardingOverlay />}
            {user && <KetoScanFAB />}
            {user && <SpiritAgentFAB />}
            {user && <SpiritAwakening />}
          </BrowserRouter>
          <Toaster position="top-right" />
          
          {/* Legal Consent Modal */}
          {user && showLegalConsent && (
            <LegalConsentModal
              isOpen={showLegalConsent}
              onClose={() => !requiresLegalAcceptance && setShowLegalConsent(false)}
              onAccept={handleLegalAcceptance}
            />
          )}
          
          {/* Keto Buddy Tour - Guided onboarding experience */}
          {user && (
            <KetoBuddyTour
              isOpen={showTour}
              onClose={() => {
                setShowTour(false);
                // Persist dismissal so tour does not re-fire on next render/session
                if (user?.id) {
                  localStorage.setItem(`keto_buddy_tour_state_${user.id}`, 'completed');
                }
              }}
              onComplete={handleTourComplete}
            />
          )}
          
          {/* Admin Plan Simulator - Only visible for admin users */}
          <PlanSimulator />
          
          {/* Upsell Banner - Shows contextual offers */}
          {user && <UpsellBanner position="bottom" />}
        </div>
      </SubscriptionProvider>
    </ThemeProvider>
    </AuthContext.Provider>
    </ErrorBoundary>
  );
}

export default App;